/**
 * navigation/platformAuthRedirect.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Billing does NOT own login/signup. The SK Platform owns the only auth
 * screens for every product. When this app has no valid session we bounce the
 * user to the centralized platform auth, carrying the product handoff:
 *     ?product=billing&return_to=<where they were>&state=<nonce>
 * plus a short-lived `sk_auth_state` cookie (parent-domain scoped) the platform
 * verifies (CSRF / first-party-initiation). After sign-in the platform sets the
 * shared `.softknack.com` session cookie and redirects back here.
 *
 * (Mirrors SK-Catalog / SK-Upsell. Web-only; no-op on native.)
 */
import { Platform } from 'react-native';

// SK Platform auth entry point, auto-selected by hostname so one build works
// everywhere: localhost → local platform dev; *.dev.* / *.staging.* → those;
// anything else → prod.
function resolvePlatformAuthUrl(): string {
  if (typeof window === 'undefined') return 'https://platform.softknack.com/';
  const h = window.location.hostname;
  if (/^(localhost|127\.0\.0\.1)$/.test(h)) return 'http://localhost:3009/';
  if (h.includes('.dev.')) return 'https://platform.dev.softknack.com/';
  if (h.includes('.staging.')) return 'https://platform.staging.softknack.com/';
  return 'https://platform.softknack.com/';
}

const PLATFORM_AUTH_URL = resolvePlatformAuthUrl();
const PRODUCT = 'billing';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  (window.crypto as Crypto).getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

function setStateCookie(state: string): void {
  const parts = window.location.hostname.split('.');
  // Parent-domain scope on real hosts (.softknack.com); host-only on localhost
  // (cookies ignore port, so it's still shared with the platform on another port).
  const domain = parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
    ? `; domain=.${parts.slice(-2).join('.')}`
    : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATE_COOKIE}=${state}; Max-Age=600; path=/; SameSite=Lax${domain}${secure}`;
}

// Fire at most once per page load (boot probe + a parallel 401 could both call).
let _redirecting = false;

/** Bounce to the centralized platform auth, returning here after sign-in. */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const state = randomState();
  setStateCookie(state);

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * Send a signed-in user whose org did not buy Billing to the platform's
 * "not in your plan" page (see navigation/planGate). The session stays: the
 * org's own console still calls Billing. `replace`, so Back does not land on a
 * Billing that would only send them here again. Web-only; no-op on native.
 */
export function redirectToNotInPlan(): void {
  if (!isWeb() || _redirecting) return;
  _redirecting = true;

  const url = new URL('not-in-plan', PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  window.location.replace(url.toString());
}
